import React from 'react';
import Box from '@mui/joy/Box';
import Card from '@mui/joy/Card';
import Typography from '@mui/joy/Typography';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemButton from '@mui/joy/ListItemButton';
import ListItemContent from '@mui/joy/ListItemContent';
import Chip from '@mui/joy/Chip';
import { FileText as Description, CheckCircle, XCircle as Error, Clock as Schedule } from '@phosphor-icons/react';
import type { PolicyAnalysis } from '../types';

interface PolicyHistoryProps {
  analyses: PolicyAnalysis[];
  onSelectAnalysis: (analysis: PolicyAnalysis) => void;
  currentAnalysisId?: number;
}

const PolicyHistory: React.FC<PolicyHistoryProps> = ({ 
  analyses, 
  onSelectAnalysis,
  currentAnalysisId 
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle color="var(--joy-palette-success-500)" />;
      case 'failed':
        return <Error color="var(--joy-palette-danger-500)" />;
      case 'processing':
        return <Schedule color="var(--joy-palette-warning-500)" />;
      default:
        return <Description />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'danger';
      case 'processing':
        return 'warning';
      default:
        return 'neutral';
    }
  };

  if (analyses.length === 0) {
    return null;
  }

  return (
    <Card>
      <Typography level="h4" sx={{ mb: 2 }}>
        Recent policy reviews
      </Typography>
      
      <List
        sx={{
          '--ListItem-radius': '8px',
          '--List-gap': '8px',
        }}
      >
        {analyses.map((analysis) => (
          <ListItem key={analysis.id}>
            <ListItemButton
              selected={analysis.id === currentAnalysisId}
              onClick={() => onSelectAnalysis(analysis)}
              sx={{
                flexDirection: 'column',
                alignItems: 'stretch',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', flexWrap: 'wrap', gap: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'start', flexWrap: 'wrap', minWidth: 0 }}>
                  {getStatusIcon(analysis.status)}
                  <ListItemContent>
                    <Typography level="body-md" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
                      {analysis.file_name}
                    </Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                      {analysis.contact_name || 'No contact'} • {formatDate(analysis.uploaded_at)}
                    </Typography>
                  </ListItemContent>
                </Box>
                
                <Chip 
                  size="sm" 
                  color={getStatusColor(analysis.status)}
                  variant="soft"
                >
                  {analysis.status === 'needs_review' ? 'Needs review' : analysis.status}
                </Chip>
              </Box>
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Card>
  );
};

export default PolicyHistory;
